import type { NativeInvoicePageBoxCss } from "../helpers/nativeInvoicePagination";

export const NATIVE_INVOICE_DOCUMENT_SCREEN_PAGE_GAP_PX = 16;
export const NATIVE_INVOICE_DOCUMENT_SCREEN_PAGE_BORDER_PX = 1;

export interface NativeInvoiceDocumentPreviewFrameSize {
    width: string;
    height: string;
}

export interface NativeInvoiceDocumentPreviewFrameSizeInput {
    pageBox: NativeInvoicePageBoxCss;
    pageCount: number;
}

export interface NativeInvoiceDocumentCssInput {
    pageBox: NativeInvoicePageBoxCss;
    backgroundColor: string;
    fontFamily: string;
    monogramBackground: string;
    accentColor: string;
    // Primary document text color (headings, values, line items) plus its two
    // derived tints (secondary/muted copy, hairline borders) — see
    // getInvoiceTextColor / getInvoiceMutedTextColor / getInvoiceSubtleTextColor
    // / getInvoiceBorderColor in common/constants/invoiceDesign.
    textColor: string;
    mutedTextColor: string;
    subtleTextColor: string;
    borderColor: string;
}

export function nativeInvoiceDocumentPreviewFrameSize({
    pageBox,
    pageCount,
}: NativeInvoiceDocumentPreviewFrameSizeInput): NativeInvoiceDocumentPreviewFrameSize {
    const gaps =
        Math.max(pageCount - 1, 0) * NATIVE_INVOICE_DOCUMENT_SCREEN_PAGE_GAP_PX;
    const borders =
        pageCount * NATIVE_INVOICE_DOCUMENT_SCREEN_PAGE_BORDER_PX * 2;
    return {
        width: pageBox.width,
        height: `calc(${pageCount} * ${pageBox.height} + ${gaps + borders}px)`,
    };
}

export function nativeInvoiceDocumentCss({
    pageBox,
    backgroundColor,
    fontFamily,
    monogramBackground,
    accentColor,
    textColor,
    mutedTextColor,
    subtleTextColor,
    borderColor,
}: NativeInvoiceDocumentCssInput): string {
    return `
            * { box-sizing: border-box; overflow-wrap: anywhere; }
            @page {
                size: ${pageBox.width} ${pageBox.height};
                margin: 0;
            }
            body {
                background: ${backgroundColor};
                color: ${textColor};
                font-family: ${fontFamily};
                font-size: 14px;
                line-height: 1.45;
                margin: 0;
                padding: 0;
                font-variant-numeric: tabular-nums;
                font-feature-settings: "tnum" 1;
            }
            .page {
                background: ${backgroundColor};
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                height: ${pageBox.height};
                justify-content: flex-start;
                padding: 48px 48px 20px;
                page-break-after: always;
                break-after: page;
                width: 100%;
            }
            .page:last-child {
                page-break-after: auto;
                break-after: auto;
            }
            .pageBody {
                display: flex;
                flex-direction: column;
                flex-grow: 0;
            }
            .masthead {
                align-items: flex-start;
                display: flex;
                justify-content: space-between;
                margin-bottom: 40px;
            }
            .invoiceHeading {
                color: ${textColor};
                font-size: 32px;
                font-weight: 500;
                letter-spacing: -0.8px;
                line-height: 42px;
                margin: 0;
            }
            .brandLogo {
                border-radius: 8px;
                height: 56px;
                margin-left: auto;
                max-width: 160px;
                object-fit: contain;
            }
            .brandMonogram {
                align-items: center;
                background: ${monogramBackground};
                border-radius: 8px;
                color: ${accentColor};
                display: flex;
                font-size: 28px;
                font-weight: 500;
                height: 56px;
                justify-content: center;
                letter-spacing: -0.56px;
                margin-left: auto;
                width: 56px;
            }
            .infoGrid {
                display: grid;
                gap: 24px;
                grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 1.1fr);
                margin-bottom: 36px;
            }
            .metaColumn { display: flex; flex-direction: column; gap: 16px; }
            .infoBlock { display: flex; flex-direction: column; gap: 4px; }
            .label {
                color: ${mutedTextColor};
                font-size: 12px;
                font-weight: 400;
                line-height: 18px;
                margin-bottom: 4px;
            }
            .value {
                color: ${textColor};
                font-size: 14px;
                font-weight: 500;
                letter-spacing: -0.07px;
                line-height: 20px;
            }
            .partyColumn { display: flex; flex-direction: column; }
            .partyBlock {
                color: ${textColor};
                font-size: 14px;
                font-weight: 500;
                letter-spacing: -0.07px;
                line-height: 20px;
            }
            .partyName { font-weight: 500; }
            .partyMeta {
                color: ${mutedTextColor};
                font-size: 12px;
                font-weight: 400;
                letter-spacing: 0;
                line-height: 18px;
            }
            .lineTable {
                border-collapse: collapse;
                margin-bottom: 6px;
                width: 100%;
            }
            .lineTable th {
                border-bottom: 1px solid ${borderColor};
                color: ${mutedTextColor};
                font-size: 12px;
                font-weight: 400;
                line-height: 18px;
                padding: 8px 4px;
                text-align: left;
            }
            .lineTable td {
                border-bottom: 1px solid ${borderColor};
                color: ${textColor};
                font-size: 14px;
                padding: 12px 4px;
                vertical-align: top;
            }
            /* A page that ends with the table (continuation pages) would draw
               the last row's border right above the footer's border-top;
               dropping the row border avoids the doubled line. */
            .page:has(> .documentFooter) .pageBody > .lineTable:last-child tbody tr:last-child td {
                border-bottom: none;
            }
            .lineTable .numericCol,
            .lineTable th.numericCol {
                text-align: right;
                white-space: nowrap;
                width: 100px;
            }
            .lineTable .narrowCol,
            .lineTable th.narrowCol {
                width: 80px;
            }
            .lineName {
                color: ${textColor};
                font-size: 14px;
                font-weight: 500;
                letter-spacing: -0.07px;
                line-height: 20px;
            }
            .lineDescription { color: ${mutedTextColor}; font-size: 12px; line-height: 18px; margin-top: 4px; }
            .totals { margin-left: auto; width: 280px; }
            .totalRow {
                align-items: center;
                color: ${textColor};
                display: flex;
                font-size: 14px;
                gap: 4px;
                padding: 6px 0;
            }
            .totalRowLabel { flex: 1; text-align: right; }
            .totalRowValue { padding-right: 4px; text-align: right; width: 120px; }
            .totalRowGrand {
                font-weight: 500;
                letter-spacing: -0.07px;
                margin-top: 8px;
                padding-top: 12px;
            }
            .totalRowGrandDivider {
                border-top: 1px solid ${borderColor};
            }
            .bottomColumns { margin-top: 36px; page-break-inside: avoid; }
            .bottomHeadingRow {
                border-bottom: 1px solid ${borderColor};
                display: grid;
                gap: 32px;
                grid-template-columns: 1fr 1fr;
                padding-bottom: 8px;
            }
            .bottomContentRow {
                display: grid;
                gap: 32px;
                grid-template-columns: 1fr 1fr;
                margin-top: 12px;
            }
            .bottomRowSingle { grid-template-columns: 1fr; }
            .bottomHeading {
                color: ${mutedTextColor};
                font-size: 12px;
                font-weight: 400;
                line-height: 18px;
            }
            .bottomText {
                color: ${textColor};
                font-size: 14px;
                margin: 0;
                white-space: pre-line;
            }
            .documentFooter {
                border-top: 1px solid ${borderColor};
                color: ${subtleTextColor};
                display: flex;
                flex-shrink: 0;
                font-size: 12px;
                gap: 16px;
                line-height: 18px;
                margin-top: auto;
                padding-top: 16px;
                white-space: pre-line;
            }
            .documentFooterText {
                flex: 1 1 auto;
                min-width: 0;
                text-align: inherit;
            }
            .documentFooterPagination {
                flex: none;
                white-space: nowrap;
            }
            @media screen {
                html, body { background: ${backgroundColor}; }
                .page {
                    margin: 0 auto ${NATIVE_INVOICE_DOCUMENT_SCREEN_PAGE_GAP_PX}px;
                    width: ${pageBox.width};
                }
                .page:last-child { margin-bottom: 0; }
            }
`;
}
